import React, { useState, useEffect } from 'react';
import {
  X,
  Sparkles,
  CheckCircle2,
  CalendarClock,
  AlertCircle
} from 'lucide-react';
import { BookingRecord } from '../utils/excelExport';
import { rescheduleBooking } from '../services/bookingsService';
import {
  TIME_SLOTS,
  getLocalTodayDateString,
  isSlotAvailableTimeWise,
  getNextSlot,
  isLongServiceOrder,
  calculateBookingDuration,
  normalizeToFourSlots
} from '../services/slotService';

interface RescheduleBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingRecord | null;
  adminPin?: string;
  onSuccess: (updated: BookingRecord) => void;
}

export const RescheduleBookingModal: React.FC<RescheduleBookingModalProps> = ({
  isOpen,
  onClose,
  booking,
  adminPin,
  onSuccess
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [rescheduleReason, setRescheduleReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const todayStr = getLocalTodayDateString();

  // Generate quick date chips for next 7 days
  const quickDates = React.useMemo(() => {
    const list: Array<{ dateStr: string; label: string; subLabel: string }> = [];
    const base = new Date();

    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${day}`;

      let label = '';
      if (i === 0) label = 'Today';
      else if (i === 1) label = 'Tomorrow';
      else {
        label = d.toLocaleDateString('en-IN', { weekday: 'short' });
      }

      const subLabel = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
      list.push({ dateStr, label, subLabel });
    }
    return list;
  }, []);

  // Initialize selected values whenever modal opens or booking changes
  useEffect(() => {
    if (isOpen && booking) {
      setErrorMessage('');
      setRescheduleReason('');
      setIsSubmitting(false);

      // Default to tomorrow or booking's current date if in future
      if (booking.preferredDate && booking.preferredDate >= todayStr) {
        setSelectedDate(booking.preferredDate);
      } else {
        setSelectedDate(quickDates[1]?.dateStr || todayStr);
      }

      const initialSlot = booking.preferredSlot ? normalizeToFourSlots(booking.preferredSlot) : TIME_SLOTS[0];
      setSelectedSlot(initialSlot);
    }
  }, [isOpen, booking, todayStr, quickDates]);

  if (!isOpen || !booking) return null;

  const isCurrentSchedule =
    selectedDate === booking.preferredDate && selectedSlot === booking.preferredSlot;

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate) {
      setErrorMessage('Please select a new appointment date.');
      return;
    }
    if (!selectedSlot) {
      setErrorMessage('Please select a time slot.');
      return;
    }

    if (isCurrentSchedule) {
      setErrorMessage('Please select a different date or time slot to reschedule.');
      return;
    }

    // Validate that slot is not in the past if today is selected
    if (selectedDate === todayStr && !isSlotAvailableTimeWise(selectedSlot, selectedDate)) {
      setErrorMessage('Selected time slot is already past or too soon. Please pick a later slot or date.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const res = await rescheduleBooking(booking.bookingRef, selectedDate, selectedSlot, {
        adminPin,
        reason: rescheduleReason.trim() || undefined
      });

      if (res.success && res.updatedBooking) {
        onSuccess(res.updatedBooking);
        onClose();
      } else {
        setErrorMessage(res.error || 'Failed to reschedule appointment. Please try again.');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Network error occurred while rescheduling.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 99999 }}>
      <div
        className="reschedule-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff',
          borderRadius: '20px',
          width: '94%',
          maxWidth: '520px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.22)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '1.25rem 1.4rem 1rem',
            borderBottom: '1px solid rgba(125, 26, 56, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #fffafd 0%, #fff6f8 100%)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'rgba(125, 26, 56, 0.1)',
                color: 'var(--blishka-berry)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <CalendarClock size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '1.15rem',
                    fontWeight: 800,
                    color: 'var(--color-text-title)'
                  }}
                >
                  Reschedule Appointment
                </h3>
                {adminPin && (
                  <span
                    style={{
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      background: 'var(--blishka-berry)',
                      color: '#ffffff',
                      padding: '0.1rem 0.45rem',
                      borderRadius: '6px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Admin
                  </span>
                )}
              </div>
              <p
                style={{
                  margin: '0.15rem 0 0',
                  fontSize: '0.78rem',
                  color: 'var(--color-text-muted)'
                }}
              >
                Ref: <strong style={{ color: 'var(--blishka-berry)' }}>{booking.bookingRef}</strong> · {booking.clientName}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            style={{ position: 'static', transform: 'none' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Content */}
        <form
          onSubmit={handleConfirm}
          style={{
            padding: '1.25rem 1.4rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.15rem'
          }}
        >
          {/* Current Schedule Summary Badge */}
          <div
            style={{
              background: '#fdf2f4',
              border: '1px solid rgba(125, 26, 56, 0.15)',
              borderRadius: '12px',
              padding: '0.75rem 0.95rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              fontSize: '0.82rem'
            }}
          >
            <div>
              <span
                style={{
                  display: 'block',
                  fontSize: '0.7rem',
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}
              >
                Current Schedule:
              </span>
              <strong style={{ color: 'var(--blishka-berry)', fontSize: '0.88rem' }}>
                {booking.preferredDate} ({booking.preferredSlot})
              </strong>
            </div>

            <div
              style={{
                fontSize: '0.74rem',
                color: '#2e7d32',
                fontWeight: 700,
                background: '#ffffff',
                padding: '0.2rem 0.6rem',
                borderRadius: '8px',
                border: '1px solid rgba(46, 125, 50, 0.2)'
              }}
            >
              ✓ Pay After Service
            </div>
          </div>

          {/* STEP 1: SELECT NEW DATE */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.84rem',
                fontWeight: 700,
                color: 'var(--color-text-title)',
                marginBottom: '0.5rem'
              }}
            >
              1. Choose New Date *
            </label>

            {/* Quick Next 7 Days Pills */}
            <div
              style={{
                display: 'flex',
                gap: '0.45rem',
                overflowX: 'auto',
                paddingBottom: '0.4rem',
                scrollbarWidth: 'none'
              }}
            >
              {quickDates.map((item) => {
                const isSelected = selectedDate === item.dateStr;
                return (
                  <button
                    key={item.dateStr}
                    type="button"
                    onClick={() => setSelectedDate(item.dateStr)}
                    style={{
                      flex: '0 0 auto',
                      padding: '0.45rem 0.75rem',
                      borderRadius: '12px',
                      border: isSelected
                        ? '1.5px solid var(--blishka-berry)'
                        : '1px solid rgba(0,0,0,0.12)',
                      background: isSelected ? 'var(--blishka-berry)' : '#ffffff',
                      color: isSelected ? '#ffffff' : 'var(--color-text-title)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span style={{ fontSize: '0.78rem', fontWeight: 800 }}>{item.label}</span>
                    <span
                      style={{
                        fontSize: '0.68rem',
                        opacity: isSelected ? 0.9 : 0.65,
                        fontWeight: 600
                      }}
                    >
                      {item.subLabel}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Custom Date Input for dates beyond 7 days */}
            <div style={{ marginTop: '0.45rem' }}>
              <input
                type="date"
                min={todayStr}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.55rem 0.85rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(0,0,0,0.15)',
                  fontSize: '0.84rem',
                  fontWeight: 600,
                  color: 'var(--color-text-title)',
                  background: '#fafafa'
                }}
              />
            </div>
          </div>

          {/* STEP 2: SELECT NEW TIME SLOT */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.84rem',
                fontWeight: 700,
                color: 'var(--color-text-title)',
                marginBottom: '0.5rem'
              }}
            >
              2. Choose New Doorstep Slot (10:00 AM – 6:00 PM) *
            </label>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.6rem'
              }}
            >
              {TIME_SLOTS.map((slot) => {
                const isSelected = selectedSlot === slot;
                const isPast =
                  selectedDate === todayStr && !isSlotAvailableTimeWise(slot, selectedDate);

                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={isPast}
                    onClick={() => setSelectedSlot(slot)}
                    style={{
                      padding: '0.65rem 0.5rem',
                      borderRadius: '12px',
                      border: isSelected
                        ? '1.5px solid var(--blishka-berry)'
                        : '1px solid rgba(0,0,0,0.12)',
                      background: isSelected
                        ? 'var(--blishka-berry)'
                        : isPast
                        ? '#f3f4f6'
                        : '#ffffff',
                      color: isSelected
                        ? '#ffffff'
                        : isPast
                        ? '#9ca3af'
                        : 'var(--color-text-title)',
                      cursor: isPast ? 'not-allowed' : 'pointer',
                      fontSize: '0.82rem',
                      fontWeight: isSelected ? 800 : 600,
                      textAlign: 'center',
                      opacity: isPast ? 0.6 : 1,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {slot}
                    {isPast && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: '0.62rem',
                          color: '#9ca3af',
                          marginTop: '2px'
                        }}
                      >
                        Past
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {booking && isLongServiceOrder(calculateBookingDuration(booking.items)) && (
              <div
                style={{
                  background: '#fff7ed',
                  border: '1px solid #fed7aa',
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.76rem',
                  color: '#9a3412',
                  marginTop: '0.6rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <span>
                  ⚡ <strong>Long Appointment Notice:</strong> This appointment takes ~{calculateBookingDuration(booking.items)} mins (over 2.5 hrs). Consecutive slot ({selectedSlot ? (getNextSlot(selectedSlot) || 'evening window') : 'next window'}) will also be reserved for your beautician.
                </span>
              </div>
            )}
          </div>

          {/* STEP 3: REASON / NOTE (OPTIONAL) */}
          <div>
            <label
              htmlFor="reschedule-reason"
              style={{
                display: 'block',
                fontSize: '0.84rem',
                fontWeight: 700,
                color: 'var(--color-text-title)',
                marginBottom: '0.35rem'
              }}
            >
              Reason / Special Note (Optional)
            </label>
            <input
              id="reschedule-reason"
              type="text"
              placeholder={
                adminPin
                  ? 'e.g. Client requested timing change on phone'
                  : 'e.g. Shifted due to work schedule / guest arrival'
              }
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.target.value)}
              style={{
                width: '100%',
                padding: '0.55rem 0.85rem',
                borderRadius: '10px',
                border: '1px solid rgba(0,0,0,0.15)',
                fontSize: '0.82rem',
                color: 'var(--color-text-title)'
              }}
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                background: 'rgba(211, 47, 47, 0.08)',
                border: '1px solid rgba(211, 47, 47, 0.3)',
                color: '#d32f2f',
                padding: '0.6rem 0.85rem',
                borderRadius: '10px',
                fontSize: '0.8rem'
              }}
            >
              <AlertCircle size={15} style={{ flexShrink: 0 }} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* New Schedule Preview Pill */}
          {!isCurrentSchedule && selectedDate && selectedSlot && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(125,26,56,0.06) 0%, rgba(216,131,149,0.1) 100%)',
                border: '1px dashed var(--blishka-berry)',
                borderRadius: '12px',
                padding: '0.75rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.65rem'
              }}
            >
              <Sparkles size={18} style={{ color: 'var(--blishka-berry)', flexShrink: 0 }} />
              <div style={{ fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--color-text-muted)', display: 'block', fontSize: '0.72rem' }}>
                  New Appointment Timing:
                </span>
                <strong style={{ color: 'var(--blishka-berry)' }}>
                  {selectedDate} at {selectedSlot}
                </strong>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: '0.5rem',
              paddingTop: '0.75rem',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-outline"
              style={{ flex: 1, padding: '0.65rem', borderRadius: '12px', fontSize: '0.86rem' }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting || isCurrentSchedule}
              className="btn btn-berry"
              style={{
                flex: 2,
                padding: '0.65rem',
                borderRadius: '12px',
                fontSize: '0.86rem',
                fontWeight: 700,
                opacity: isSubmitting || isCurrentSchedule ? 0.6 : 1,
                cursor: isSubmitting || isCurrentSchedule ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.45rem'
              }}
            >
              {isSubmitting ? (
                <span>Rescheduling...</span>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  <span>Confirm Reschedule</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
